import { apiRequest } from '../api';
import { onLocaleChange, t } from '../i18n';
import type { TranslationKey } from '../i18n/types';
import { navigateTo } from '../router';
import { state } from '../state';
import type { GameListItem } from '../types-games';
import { tierLetter, type Race } from '../types';

import terranIcon from '../../assets/race/terran.svg';
import zergIcon from '../../assets/race/zerg.svg';
import protossIcon from '../../assets/race/protoss.svg';
import randomIcon from '../../assets/race/random.svg';

const RACE_ICON_URL: Record<Race, string> = { T: terranIcon, Z: zergIcon, P: protossIcon, R: randomIcon };
const RACE_LABELS: Record<Race, string> = { T: 'Terran', Z: 'Zerg', P: 'Protoss', R: 'Random' };

type Mode = 'all' | 'solo' | 'team' | 'ffa-league' | 'team-ffa';

/** Сезонные зачёты: только победы/игры по конкретному формату, без ELO и активности. */
const isSeasonMode = (mode: Mode): boolean => mode === 'ffa-league' || mode === 'team-ffa';

/** Режимы, где есть под-переключатель «личный зачёт / связки». */
const isTeamMode = (mode: Mode): boolean => mode === 'team' || mode === 'team-ffa';

let currentMode: Mode = 'all';

/** Подрежим командного зачёта: личный (как раньше) или связки по 2 игрока. */
type TeamView = 'players' | 'pairs';
type TeamMode = 'team' | 'team-ffa';
const TEAM_VIEW_STORAGE_PREFIX = 'leaderboard.team.view.';

/** В «Командных FFA» по умолчанию показываем связки, в сезонном 2x2 — личный зачёт. */
function readTeamView(mode: TeamMode): TeamView {
    try {
        const stored = localStorage.getItem(TEAM_VIEW_STORAGE_PREFIX + mode);
        if (stored === 'pairs' || stored === 'players') return stored;
    } catch {
        // localStorage может быть недоступен
    }
    return mode === 'team' ? 'pairs' : 'players';
}

let teamView: TeamView = 'players';

/** Отдельный фильтр: считать только игры за последние 30 дней (не для сезонных зачётов). */
let last30d = false;

type SortKey = 'tier' | 'name' | 'activity' | 'games' | 'wins' | 'winrate' | 'avg_place' | 'elo';
type SortDirection = 'asc' | 'desc';

let sortKey: SortKey = 'elo';
let sortDirection: SortDirection = 'desc';

let cachedLeaderboard: LeaderboardEntry[] = [];

/** id игрока, чья строка сейчас развёрнута (график прогресса), либо null. */
let expandedPlayerId: number | null = null;
/** Ключ «a:b» раскрытой связки (график прогресса пары), либо null. */
let expandedPairKey: string | null = null;
/** AbortController текущей загрузки истории для раскрытой строки. */
let detailAbort: AbortController | null = null;

/** Имя игрока из URL (?player=...), чей график надо открыть после загрузки таблицы. */
let pendingOpenPlayer: string | null = null;

// Живой пересчёт текста при смене языка: таблица, переключатель и раскрытый график.
onLocaleChange(() => {
    const container = document.getElementById('leaderboard-container');
    if (!container) return;
    renderModeSwitch();
    // Перерисовываем из кэша даже просроченного — иначе блок останется на старом языке.
    const hof = hofCache.get(hofPeriod)?.data;
    if (hof) renderHallOfFame(hofMountPoint(container), hof);
    if (isTeamMode(currentMode) && teamView === 'pairs') {
        if (cachedPairs.length > 0) renderPairs(applyPairSort(cachedPairs), container, isSeasonMode(currentMode));
        else renderModeSwitch();
        return;
    }
    if (cachedLeaderboard.length === 0) return;
    renderLeaderboard(applySort(cachedLeaderboard), container);
    if (expandedPlayerId) {
        const p = cachedLeaderboard.find((x) => x.player_id === expandedPlayerId);
        const tr = container.querySelector(`tr[data-lb-player="${expandedPlayerId}"]`);
        if (p && tr instanceof HTMLTableRowElement) openDetail(p, tr);
    }
});

/** Равные основные значения — выше тот, у кого лучше (меньше) среднее место. */
function compareByAvgPlace(a: number | null, b: number | null): number {
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return a - b;
}

function applySort(players: LeaderboardEntry[]): LeaderboardEntry[] {
    const dir = sortDirection === 'asc' ? 1 : -1;

    return [...players].sort((a, b) => {
        let av: number | string | null;
        let bv: number | string | null;

        switch (sortKey) {
            case 'tier':
                av = a.ghost_tier;
                bv = b.ghost_tier;
                break;
            case 'name':
                av = a.name.toLowerCase();
                bv = b.name.toLowerCase();
                break;
            case 'activity':
                av = a.activity_score;
                bv = b.activity_score;
                break;
            case 'games':
                av = a.games_played;
                bv = b.games_played;
                break;
            case 'wins':
                av = a.wins;
                bv = b.wins;
                break;
            case 'winrate':
                av = a.winrate;
                bv = b.winrate;
                break;
            case 'avg_place':
                av = a.avg_place;
                bv = b.avg_place;
                break;
            case 'elo':
                av = a.elo;
                bv = b.elo;
                break;
        }

        // null всегда в конец, независимо от направления
        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;

        if (typeof av === 'string' && typeof bv === 'string') {
            return av.localeCompare(bv, 'ru') * dir;
        }
        const diff = (Number(av) - Number(bv)) * dir;
        // При равенстве (например, одинаковые победы) — по среднему месту.
        return diff !== 0 ? diff : compareByAvgPlace(a.avg_place, b.avg_place);
    });
}

function handleSortClick(key: SortKey): void {
    if (sortKey === key) {
        sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
        sortKey = key;
        // Для текстовых — asc по умолчанию, для чисел — desc (ср. место — asc: меньше лучше)
        sortDirection = key === 'name' || key === 'avg_place' ? 'asc' : 'desc';
    }

    const container = document.getElementById('leaderboard-container');
    if (!container) return;
    renderLeaderboard(applySort(cachedLeaderboard), container);
    renderModeSwitch();
}

function renderModeSwitch(): void {
    const container = document.getElementById('leaderboard-container');
    if (!container) return;

    let switcher = document.getElementById('leaderboard-mode-switch');
    if (!switcher) {
        switcher = document.createElement('div');
        switcher.id = 'leaderboard-mode-switch';
        switcher.className = 'leaderboard-mode-switch';
    } else {
        switcher.innerHTML = '';
    }

    const modes: Array<{ key: Mode; label: string }> = [
        { key: 'all', label: t('leaderboard.mode_all') },
        { key: 'solo', label: t('leaderboard.mode_solo') },
        { key: 'team', label: t('leaderboard.mode_team') },
        { key: 'ffa-league', label: t('leaderboard.mode_league_s3') },
        { key: 'team-ffa', label: t('leaderboard.mode_team_s1') },
    ];

    for (const m of modes) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'view-toggle-btn' + (currentMode === m.key ? ' is-active' : '');
        btn.textContent = m.label;
        btn.addEventListener('click', () => {
            void loadLeaderboard(m.key);
        });
        switcher.appendChild(btn);
    }

    // Отдельный фильтр «последние 30 дней» — работает для all/solo/team, не для
    // сезонных зачётов и не для связок (у связки отдельный MMR по всем играм).
    const disabled = isSeasonMode(currentMode) || (isTeamMode(currentMode) && teamView === 'pairs');
    const label = document.createElement('label');
    label.className = 'lb-30d-filter' + (disabled ? ' is-disabled' : '');
    const box = document.createElement('input');
    box.type = 'checkbox';
    box.checked = last30d;
    box.disabled = disabled;
    box.addEventListener('change', () => {
        last30d = box.checked;
        void loadLeaderboard(currentMode);
    });
    const span = document.createElement('span');
    span.textContent = t('leaderboard.filter_30d');
    label.append(box, span);
    switcher.appendChild(label);

    // Кнопка-переход к подробному объяснению MMR (нет в сезонных зачётах и у связок).
    let helpBtn: HTMLButtonElement | null = null;
    if (!disabled && !(isTeamMode(currentMode) && teamView === 'pairs')) {
        helpBtn = document.createElement('button');
        helpBtn.type = 'button';
        helpBtn.className = 'lb-explain-btn';
        helpBtn.textContent = t('leaderboard.explain_button');
        helpBtn.addEventListener('click', () => {
            document
                .getElementById('leaderboard-explain')
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    }

    // Верхняя панель: слева режимы + фильтр, справа кнопка объяснения.
    let topbar = container.querySelector<HTMLElement>('#leaderboard-topbar');
    if (!topbar) {
        topbar = document.createElement('div');
        topbar.id = 'leaderboard-topbar';
        topbar.className = 'leaderboard-topbar';
        container.prepend(topbar);
    }
    topbar.replaceChildren(switcher);
    if (helpBtn) topbar.appendChild(helpBtn);

    // Под-переключатель «личный зачёт / связки» — отдельной строкой под основным.
    container.querySelector('#leaderboard-subswitch')?.remove();
    if (isTeamMode(currentMode)) {
        const sub = document.createElement('div');
        sub.id = 'leaderboard-subswitch';
        sub.className = 'leaderboard-subswitch';
        const views: Array<{ key: TeamView; label: string }> = [
            { key: 'players', label: t('leaderboard.team_view_players') },
            { key: 'pairs', label: t('leaderboard.team_view_pairs') },
        ];
        for (const v of views) {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'view-toggle-btn' + (teamView === v.key ? ' is-active' : '');
            btn.textContent = v.label;
            btn.addEventListener('click', () => {
                if (teamView === v.key) return;
                teamView = v.key;
                expandedPlayerId = null;
                expandedPairKey = null;
                detailAbort?.abort();
                detailAbort = null;
                try {
                    localStorage.setItem(TEAM_VIEW_STORAGE_PREFIX + currentMode, teamView);
                } catch {
                    // localStorage может быть недоступен
                }
                void loadLeaderboard(currentMode);
            });
            sub.appendChild(btn);
        }
        topbar.insertAdjacentElement('afterend', sub);
    }
}

// ============================================================
// «Зал славы» недели — витрина достижений над таблицей
// ============================================================

/** Период витрины: неделя, месяц, год. */
type HofPeriod = 'week' | 'month' | 'year';

type HofKey =
    | 'wins'
    | 'activity'
    | 'climb'
    | 'mmr'
    | 'streak'
    | 'place'
    | 'days'
    | 'maps'
    | 'upset'
    | 'podium'
    | 'stability'
    | 'rookie'
    | 'sprinter'
    | 'siege'
    | 'daywins'
    | 'second'
    | 'chill';

interface HofEntry {
    player_id: number;
    name: string;
    aka: string | null;
    value: number;
    games: number;
    wins: number;
    days: number;
    maps: number;
    avg_place: number | null;
    best_streak: number;
    upset_wins: number;
    /** Попаданий в топ-3 по итоговому месту. */
    podium: number;
    /** Вторых мест (для «вечно второй»). */
    second_places: number;
    /** σ по местам (null — позиций нет). */
    place_sigma: number | null;
    /** Самая быстрая победа, мин. */
    fastest_win: number | null;
    /** Самая долгая победа, мин. */
    longest_win: number | null;
    /** Максимум побед за один игровой день. */
    day_best: number;
    rank_before: number | null;
    rank_after: number | null;
    /** Чистый прирост MMR за период. */
    mmr_delta: number;
    games_before: number;
    activity_before: number;
    activity_after: number;
    opponent_elo: number | null;
}

/** «Кровные враги» — пара игроков, а не одиночная строка. */
interface HofRivalRef {
    player_id: number;
    name: string;
    aka: string | null;
}

interface HofRival {
    a: HofRivalRef;
    b: HofRivalRef;
    games: number;
}

interface HofResponse {
    period: HofPeriod;
    days: number;
    from: string;
    to: string;
    games: number;
    players: number;
    rivals: HofRival[];
    categories: Record<HofKey, HofEntry[]>;
}

type HofCardDef =
    | {
          key: HofKey;
          icon: string;
          titleKey: TranslationKey;
          hintKey: TranslationKey;
      }
    | {
          key: 'rivals';
          icon: string;
          titleKey: TranslationKey;
          hintKey: TranslationKey;
      };

/** Порядок и подписи карточек зала славы (позитив → нейтральное → негатив). */
const HOF_CARDS: HofCardDef[] = [
    { key: 'wins', icon: '🏆', titleKey: 'leaderboard.hof_wins', hintKey: 'leaderboard.hof_wins_hint' },
    { key: 'activity', icon: '🔥', titleKey: 'leaderboard.hof_activity', hintKey: 'leaderboard.hof_activity_hint' },
    { key: 'climb', icon: '🚀', titleKey: 'leaderboard.hof_climb', hintKey: 'leaderboard.hof_climb_hint' },
    { key: 'mmr', icon: '📈', titleKey: 'leaderboard.hof_mmr', hintKey: 'leaderboard.hof_mmr_hint' },
    { key: 'streak', icon: '⚡', titleKey: 'leaderboard.hof_streak', hintKey: 'leaderboard.hof_streak_hint' },
    { key: 'place', icon: '🎯', titleKey: 'leaderboard.hof_place', hintKey: 'leaderboard.hof_place_hint' },
    { key: 'podium', icon: '🥉', titleKey: 'leaderboard.hof_podium', hintKey: 'leaderboard.hof_podium_hint' },
    { key: 'stability', icon: '⚖️', titleKey: 'leaderboard.hof_stability', hintKey: 'leaderboard.hof_stability_hint' },
    { key: 'days', icon: '🗓', titleKey: 'leaderboard.hof_days', hintKey: 'leaderboard.hof_days_hint' },
    { key: 'maps', icon: '🧭', titleKey: 'leaderboard.hof_maps', hintKey: 'leaderboard.hof_maps_hint' },
    { key: 'upset', icon: '⚔️', titleKey: 'leaderboard.hof_upset', hintKey: 'leaderboard.hof_upset_hint' },
    { key: 'rookie', icon: '🎩', titleKey: 'leaderboard.hof_rookie', hintKey: 'leaderboard.hof_rookie_hint' },
    { key: 'sprinter', icon: '⏱', titleKey: 'leaderboard.hof_sprinter', hintKey: 'leaderboard.hof_sprinter_hint' },
    { key: 'siege', icon: '🧱', titleKey: 'leaderboard.hof_siege', hintKey: 'leaderboard.hof_siege_hint' },
    { key: 'daywins', icon: '🎳', titleKey: 'leaderboard.hof_daywins', hintKey: 'leaderboard.hof_daywins_hint' },
    { key: 'rivals', icon: '🤝', titleKey: 'leaderboard.hof_rivals', hintKey: 'leaderboard.hof_rivals_hint' },
    { key: 'second', icon: '😤', titleKey: 'leaderboard.hof_second', hintKey: 'leaderboard.hof_second_hint' },
    { key: 'chill', icon: '💤', titleKey: 'leaderboard.hof_chill', hintKey: 'leaderboard.hof_chill_hint' },
];

let hofAbort: AbortController | null = null;
/** Период, который сейчас грузится (чтобы не дублировать запрос). */
let hofLoadingPeriod: HofPeriod | null = null;

const HOF_PERIOD_STORAGE = 'leaderboard.hof.period';
/** Переключатель периода над карточками. */
const HOF_PERIODS: Array<{ key: HofPeriod; labelKey: TranslationKey }> = [
    { key: 'week', labelKey: 'leaderboard.hof_period_week' },
    { key: 'month', labelKey: 'leaderboard.hof_period_month' },
    { key: 'year', labelKey: 'leaderboard.hof_period_year' },
];

/** Выбранный период переживает перезагрузку страницы. */
function readHofPeriod(): HofPeriod {
    try {
        const saved = localStorage.getItem(HOF_PERIOD_STORAGE);
        if (saved === 'week' || saved === 'month' || saved === 'year') return saved;
    } catch {
        // localStorage может быть недоступен
    }
    return 'week';
}

let hofPeriod: HofPeriod = readHofPeriod();
/** Ответы по периодам — переключение туда-обратно без запроса и мигания (TTL 60с). */
const hofCache = new Map<HofPeriod, { data: HofResponse; at: number }>();
const HOF_CACHE_TTL_MS = 60_000;

/** Свежий ответ из кэша периода. */
function cachedHof(period: HofPeriod): HofResponse | null {
    const entry = hofCache.get(period);
    if (!entry) return null;
    if (Date.now() - entry.at > HOF_CACHE_TTL_MS) {
        hofCache.delete(period);
        return null;
    }
    return entry.data;
}

/** Короткая дата «дд.мм» из ISO. */
function fmtDayMonth(iso: string): string {
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Значение карточки: главное число с учётом знака. */
function formatHofValue(key: HofKey, e: HofEntry): string {
    switch (key) {
        case 'activity':
        case 'rookie':
            return `+${e.value.toFixed(1)}`;
        case 'climb':
        case 'mmr':
            return `+${Math.round(e.value)}`;
        case 'chill':
            return `−${Math.abs(e.value).toFixed(1)}`;
        case 'place':
            return e.value.toFixed(2);
        case 'stability':
            return e.value.toFixed(2);
        case 'sprinter':
        case 'siege':
            return t('leaderboard.hof_minutes', { n: Math.round(e.value) });
        default:
            return String(Math.round(e.value));
    }
}

/** Подпись под ником лидера (число игр, места до/после, MMR соперников). */
function formatHofSub(key: HofKey, e: HofEntry): string {
    const games = t('leaderboard.hof_games', { n: e.games });
    switch (key) {
        case 'wins':
        case 'rookie': {
            const rate = e.games > 0 ? Math.round((e.wins / e.games) * 100) : 0;
            return `${games} · ${rate}%`;
        }
        case 'climb':
            return `${e.rank_before ?? '—'} → ${e.rank_after ?? '—'}`;
        case 'upset':
            return t('leaderboard.hof_opponents', { n: e.opponent_elo ?? 0 });
        case 'chill':
            return `${e.activity_before.toFixed(1)} → ${e.activity_after.toFixed(1)}`;
        case 'stability':
            return `${games} · ⌀${e.avg_place?.toFixed(2) ?? '—'}`;
        case 'sprinter':
        case 'siege':
            return t('leaderboard.hof_wins_n', { n: e.wins });
        default:
            return games;
    }
}

/** Блок «Зал славы» — над переключателем режимов (сверху страницы). */
function hofMountPoint(container: HTMLElement): HTMLElement {
    let el = container.querySelector<HTMLElement>('#leaderboard-hof');
    if (!el) {
        el = document.createElement('section');
        el.id = 'leaderboard-hof';
        el.className = 'lb-hof';
        container.prepend(el);
    }
    return el;
}

/** Загружает зал славы один раз за визит и рисует его над таблицей. */
function ensureHallOfFame(): void {
    const container = document.getElementById('leaderboard-container');
    if (!container) return;
    const mount = hofMountPoint(container);

    const cached = cachedHof(hofPeriod);
    if (cached) {
        renderHallOfFame(mount, cached);
        return;
    }
    fetchHallOfFame(mount);
}

/** Переключение периода: из кэша или новым запросом. */
function selectHofPeriod(period: HofPeriod): void {
    if (period === hofPeriod) return;
    hofPeriod = period;
    try {
        localStorage.setItem(HOF_PERIOD_STORAGE, period);
    } catch {
        // localStorage может быть недоступен
    }

    const container = document.getElementById('leaderboard-container');
    if (!container) return;
    const mount = hofMountPoint(container);

    const cached = cachedHof(period);
    if (cached) renderHallOfFame(mount, cached);
    else fetchHallOfFame(mount);
}

/** Запрос витрины за текущий период + скелетон на время загрузки. */
function fetchHallOfFame(mount: HTMLElement): void {
    const period = hofPeriod;
    if (hofLoadingPeriod === period) return;
    hofLoadingPeriod = period;

    renderHofSkeleton(mount);

    // Новый период отменяет незавершённый старый запрос.
    hofAbort?.abort();
    hofAbort = new AbortController();
    const signal = hofAbort.signal;

    apiRequest<HofResponse>(`/api/ratings/leaderboard/hall-of-fame?period=${period}`, { signal })
        .then((data) => {
            if (signal.aborted) return;
            hofCache.set(data.period ?? period, { data, at: Date.now() });
            // Пользователь мог уже переключить период, пока шёл запрос.
            if (hofPeriod === period) renderHallOfFame(mount, data);
        })
        .catch(() => {
            // Тихо убираем блок: это дополнительная витрина, не критичная для страницы.
            if (!signal.aborted) mount.replaceChildren();
        })
        .finally(() => {
            if (hofAbort?.signal === signal) {
                hofAbort = null;
                hofLoadingPeriod = null;
            }
        });
}

/** Заглушка: шапка с переключателем + пустые карточки, чтобы блок не «прыгал». */
function renderHofSkeleton(mount: HTMLElement): void {
    mount.replaceChildren(hofHead(hofPeriod, null));
    const grid = document.createElement('div');
    grid.className = 'lb-hof-grid';
    for (let i = 0; i < 6; i++) {
        const card = document.createElement('div');
        card.className = 'lb-hof-card skeleton';
        card.style.height = '106px';
        grid.appendChild(card);
    }
    mount.appendChild(grid);
}

/** Шапка блока: заголовок, переключатель периода и диапазон дат. */
function hofHead(period: HofPeriod, data: HofResponse | null): HTMLElement {
    const head = document.createElement('div');
    head.className = 'lb-hof-head';

    const title = document.createElement('h3');
    title.className = 'lb-hof-title';
    title.textContent = `🏅 ${t('leaderboard.hof_title')}`;

    const switcher = document.createElement('div');
    switcher.className = 'lb-hof-switch';
    for (const def of HOF_PERIODS) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'lb-hof-switch-btn' + (def.key === period ? ' is-active' : '');
        btn.textContent = t(def.labelKey);
        btn.addEventListener('click', () => selectHofPeriod(def.key));
        switcher.appendChild(btn);
    }

    const label = document.createElement('span');
    label.className = 'lb-hof-period';
    label.textContent = data
        ? t('leaderboard.hof_period', {
              from: fmtDayMonth(data.from),
              to: fmtDayMonth(data.to),
              n: data.games,
          })
        : '';

    head.append(title, switcher, label);
    return head;
}

/** Колонки подгоняем под 3 строки; широкая — та, где стоят «кровные враги». */
function applyHofColumns(grid: HTMLElement): void {
    const cards = Array.from(grid.children) as HTMLElement[];
    if (cards.length === 0) return;
    const cols = Math.ceil(cards.length / 3);
    const rivalsIndex = cards.findIndex((el) => el.classList.contains('lb-hof-card--rivals'));
    const wideCol = rivalsIndex >= 0 ? (rivalsIndex % cols) + 1 : -1;
    const template = Array.from({ length: cols }, (_, i) =>
        i + 1 === wideCol ? '1.4fr' : '1fr'
    ).join(' ');
    grid.style.setProperty('--hof-cols', template);
}

function renderHallOfFame(mount: HTMLElement, data: HofResponse): void {
    mount.replaceChildren(hofHead(data.period ?? hofPeriod, data));

    const grid = document.createElement('div');
    grid.className = 'lb-hof-grid';

    for (const def of HOF_CARDS) {
        if (def.key === 'rivals') {
            if ((data.rivals?.length ?? 0) > 0) grid.appendChild(buildHofRivalCard(def, data.rivals));
            continue;
        }
        const list = data.categories[def.key] ?? [];
        if (list.length === 0) continue;
        grid.appendChild(buildHofCard(def, list));
    }

    if (grid.childElementCount === 0) {
        const empty = document.createElement('p');
        empty.className = 'hint';
        empty.textContent = t('leaderboard.hof_empty');
        mount.append(empty);
        return;
    }

    applyHofColumns(grid);
    mount.appendChild(grid);
}

type HofCardMeta = { icon: string; titleKey: TranslationKey; hintKey: TranslationKey };

/** Шапка карточки (иконка + название) — общая для обычных и парных номинаций. */
function buildHofCardHead(def: HofCardMeta): HTMLElement {
    const head = document.createElement('header');
    head.className = 'lb-hof-card-head';
    const icon = document.createElement('span');
    icon.className = 'lb-hof-icon';
    icon.textContent = def.icon;
    const label = document.createElement('span');
    label.className = 'lb-hof-card-title';
    label.textContent = t(def.titleKey);
    head.append(icon, label);
    return head;
}

/** Ссылка на игрока: SPA-переход на его карточку, подсветка себя. */
function hofPlayerLink(playerId: number, name: string): HTMLAnchorElement {
    const link = document.createElement('a');
    link.className = 'lb-hof-name';
    link.href = `/?player=${encodeURIComponent(name)}`;
    link.textContent = name;
    if (state.user?.player_id && playerId === state.user.player_id) {
        link.classList.add('is-me');
    }
    link.addEventListener('click', (ev) => {
        ev.preventDefault();
        navigateTo(`/?player=${encodeURIComponent(name)}`);
    });
    return link;
}

function buildHofCard(
    def: { key: HofKey } & HofCardMeta,
    list: HofEntry[]
): HTMLElement {
    const card = document.createElement('article');
    card.className = 'lb-hof-card lb-hof-card--' + def.key;
    card.title = t(def.hintKey);
    card.appendChild(buildHofCardHead(def));

    const ol = document.createElement('ol');
    ol.className = 'lb-hof-list';

    list.forEach((e, index) => {
        const li = document.createElement('li');
        li.className = 'lb-hof-row' + (index === 0 ? ' is-first' : '');

        const pos = document.createElement('span');
        pos.className = 'lb-hof-pos';
        pos.textContent = String(index + 1);

        const mid = document.createElement('span');
        mid.className = 'lb-hof-mid';
        mid.appendChild(hofPlayerLink(e.player_id, e.name));

        if (index === 0) {
            const sub = document.createElement('span');
            sub.className = 'lb-hof-sub';
            sub.textContent = formatHofSub(def.key, e);
            mid.appendChild(sub);
        }

        const value = document.createElement('span');
        value.className = 'lb-hof-value';
        value.textContent = formatHofValue(def.key, e);

        li.append(pos, mid, value);
        ol.appendChild(li);
    });

    card.appendChild(ol);
    return card;
}

/** «Кровные враги»: в строке пара игроков и число совместных игр. */
function buildHofRivalCard(def: HofCardMeta, list: HofRival[]): HTMLElement {
    const card = document.createElement('article');
    card.className = 'lb-hof-card lb-hof-card--rivals';
    card.title = t(def.hintKey);
    card.appendChild(buildHofCardHead(def));

    const ol = document.createElement('ol');
    ol.className = 'lb-hof-list';

    list.forEach((r, index) => {
        const li = document.createElement('li');
        li.className = 'lb-hof-row' + (index === 0 ? ' is-first' : '');

        const pos = document.createElement('span');
        pos.className = 'lb-hof-pos';
        pos.textContent = String(index + 1);

        const mid = document.createElement('span');
        mid.className = 'lb-hof-mid';

        const pair = document.createElement('span');
        pair.className = 'lb-hof-pair';
        pair.append(
            hofPlayerLink(r.a.player_id, r.a.name),
            document.createTextNode(' × '),
            hofPlayerLink(r.b.player_id, r.b.name)
        );
        mid.appendChild(pair);

        if (index === 0) {
            const sub = document.createElement('span');
            sub.className = 'lb-hof-sub';
            sub.textContent = t('leaderboard.hof_games', { n: r.games });
            mid.appendChild(sub);
        }

        const value = document.createElement('span');
        value.className = 'lb-hof-value';
        value.textContent = String(r.games);

        li.append(pos, mid, value);
        ol.appendChild(li);
    });

    card.appendChild(ol);
    return card;
}

interface LeaderboardEntry {
    player_id: number;
    name: string;
    aka: string | null;
    races: Race[] | undefined;
    /** Средний тир по ghost-оценкам (как в таблице игроков). */
    ghost_tier: number | null;
    elo: number;
    games_played: number;
    wins: number;
    winrate: number;
    activity_score: number;
    quality: number;
    avg_place: number | null;
    game_days: number;
    rank: number;
    /** Место по MMR (не зависит от сортировки таблицы); в сезонных зачётах — по победам. */
    mmr_rank?: number | null;
    /** Место в предыдущий игровой день и смещение (плюс — поднялся). */
    rank_prev?: number | null;
    rank_delta?: number | null;
    /** Сколько дней держится на текущем месте. */
    place_days?: number | null;
}

let abortController: AbortController | null = null;

/** Связка двух игроков (командный зачёт по парам). */
interface PairEntry {
    player_a_id: number;
    player_b_id: number;
    player_a_name: string;
    player_a_aka: string | null;
    player_b_name: string;
    player_b_aka: string | null;
    games: number;
    wins: number;
    winrate: number | null;
    avg_place: number | null;
    /** Отдельный MMR связки (только внесезонный зачёт). */
    pair_elo?: number;
    /** Число игровых дней (сезонный зачёт). */
    game_days?: number;
    last_played: string | null;
}

/** Точка истории MMR связки (ответ /api/ratings/leaderboard/pair-history). */
interface PairHistoryPoint {
    game_id: number;
    played_at: string;
    pair_elo: number;
    elo_delta: number;
    games: number;
    wins: number;
    winrate: number;
    avg_place: number;
    place: number;
    total: number;
    is_winner: boolean;
    format: string | null;
    map: string | null;
}

type PairSortKey =
    | 'pair'
    | 'pair_elo'
    | 'games'
    | 'wins'
    | 'winrate'
    | 'avg_place'
    | 'game_days';

/** Стабильный ключ связки: id игроков по возрастанию. */
function pairKey(p: { player_a_id: number; player_b_id: number }): string {
    return `${Math.min(p.player_a_id, p.player_b_id)}:${Math.max(p.player_a_id, p.player_b_id)}`;
}

let cachedPairs: PairEntry[] = [];
let pairSortKey: PairSortKey = 'pair_elo';
let pairSortDir: SortDirection = 'desc';

function applyPairSort(pairs: PairEntry[]): PairEntry[] {
    const dir = pairSortDir === 'asc' ? 1 : -1;
    const pairName = (p: PairEntry) => `${p.player_a_name} ${p.player_b_name}`.toLowerCase();

    return [...pairs].sort((a, b) => {
        let av: number | string | null;
        let bv: number | string | null;

        switch (pairSortKey) {
            case 'pair':
                av = pairName(a);
                bv = pairName(b);
                break;
            case 'pair_elo':
                av = a.pair_elo ?? 0;
                bv = b.pair_elo ?? 0;
                break;
            case 'games':
                av = a.games;
                bv = b.games;
                break;
            case 'wins':
                av = a.wins;
                bv = b.wins;
                break;
            case 'winrate':
                av = a.winrate;
                bv = b.winrate;
                break;
            case 'avg_place':
                av = a.avg_place;
                bv = b.avg_place;
                break;
            case 'game_days':
                av = a.game_days ?? 0;
                bv = b.game_days ?? 0;
                break;
            default:
                av = a.pair_elo ?? 0;
                bv = b.pair_elo ?? 0;
                break;
        }

        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;

        if (typeof av === 'string' && typeof bv === 'string') {
            return av.localeCompare(bv, 'ru') * dir;
        }
        const diff = (Number(av) - Number(bv)) * dir;
        // При равенстве (например, одинаковые победы) — по среднему месту.
        return diff !== 0 ? diff : compareByAvgPlace(a.avg_place, b.avg_place);
    });
}

/** Загрузка таблицы связок (командный зачёт → «Связки»). */
async function loadPairs(container: HTMLElement): Promise<void> {
    const season = isSeasonMode(currentMode);

    // Сортировка по MMR не имеет смысла в сезонном зачёте — переключаемся на победы.
    if (season && pairSortKey === 'pair_elo') {
        pairSortKey = 'wins';
        pairSortDir = 'desc';
    } else if (!season && pairSortKey === 'game_days') {
        pairSortKey = 'pair_elo';
        pairSortDir = 'desc';
    }

    const table = container.querySelector<HTMLTableElement>('.leaderboard-table');
    if (!table) {
        container.innerHTML = '';
        const skeleton = document.createElement('div');
        skeleton.className = 'skeleton skeleton-block';
        skeleton.style.height = '400px';
        container.appendChild(skeleton);
    } else {
        table.classList.add('is-loading');
    }

    // У связок MMR отдельный и считается по всем совместным играм, поэтому
    // фильтр «последние 30 дней» к таблице связок не применяется.
    const query = season ? '?format=team-ffa' : '';

    try {
        const res = await apiRequest<{ pairs: PairEntry[] }>(
            `/api/ratings/leaderboard/pairs${query}`
        );
        cachedPairs = res.pairs;
        // Зал славы считается по игрокам — у связок его не показываем.
        container.querySelector('#leaderboard-hof')?.remove();
        renderPairs(applyPairSort(cachedPairs), container, season);
        renderModeSwitch();
    } catch {
        container.innerHTML = `<p class="error">${t('common.error')}</p>`;
    }
}

export function mountLeaderboard(params: URLSearchParams): void {
    const screen = document.getElementById('screen-leaderboard');
    if (screen) screen.classList.remove('hidden');

    pendingOpenPlayer = params.get('player');

    abortController = new AbortController();
    void loadLeaderboard();
}

export function unmountLeaderboard(): void {
    abortController?.abort();
    abortController = null;
    hofAbort?.abort();
    hofAbort = null;
    hofLoadingPeriod = null;
    detailAbort?.abort();
    detailAbort = null;
    expandedPlayerId = null;
    expandedPairKey = null;
    pendingOpenPlayer = null;
}

async function loadLeaderboard(mode: Mode = 'all'): Promise<void> {
    const container = document.getElementById('leaderboard-container');
    if (!container) return;

    const modeChanged = currentMode !== mode;
    currentMode = mode;

    if (modeChanged) {
        // В сезонных зачётах основной параметр — победы.
        sortKey = isSeasonMode(mode) ? 'wins' : 'elo';
        sortDirection = 'desc';
        // У каждого командного раздела свой запомненный подрежим.
        if (isTeamMode(mode)) teamView = readTeamView(mode as TeamMode);
        expandedPlayerId = null;
        expandedPairKey = null;
        detailAbort?.abort();
        detailAbort = null;
    }

    // Командный зачёт в режиме связок — отдельная таблица.
    if (isTeamMode(currentMode) && teamView === 'pairs') {
        await loadPairs(container);
        return;
    }

    // Возврат из связок: таблица пар другой структуры — пересоберём заново.
    container.querySelector('.leaderboard-table--pairs')?.remove();

    // Если таблицы ещё нет — рисуем скелетон
    let table = container.querySelector<HTMLTableElement>('.leaderboard-table');
    if (!table) {
        container.innerHTML = '';
        const skeleton = document.createElement('div');
        skeleton.className = 'skeleton skeleton-block';
        skeleton.style.height = '400px';
        container.appendChild(skeleton);
    } else {
        // Плавно затемняем текущую таблицу
        table.classList.add('is-loading');
    }

    try {
        const res = await apiRequest<{ players: LeaderboardEntry[] }>(
            `/api/ratings/leaderboard?mode=${mode}${last30d && !isSeasonMode(mode) ? '&days=30' : ''}`
        );
        cachedLeaderboard = res.players;
        renderLeaderboard(applySort(cachedLeaderboard), container);
        renderModeSwitch();
        ensureHallOfFame();
        openPendingPlayer(container);
    } catch {
        container.innerHTML = `<p class="error">${t('common.error')}</p>`;
    }
}

/** Открывает график игрока, переданного в URL (?player=...), и скроллит к нему. */
function openPendingPlayer(container: HTMLElement): void {
    const name = pendingOpenPlayer;
    pendingOpenPlayer = null;
    if (!name) return;

    const p = cachedLeaderboard.find((x) => x.name === name);
    if (!p) return;
    const tr = container.querySelector<HTMLTableRowElement>(
        `tr[data-lb-player="${p.player_id}"]`
    );
    if (!tr) return;
    openDetail(p, tr);
    tr.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

function renderLeaderboard(players: LeaderboardEntry[], container: HTMLElement): void {
    if (players.length === 0) {
        container.innerHTML = `<p class="hint">${t('leaderboard.empty')}</p>`;
        return;
    }

    // Проверяем, есть ли уже таблица
    let table = container.querySelector<HTMLTableElement>('.leaderboard-table');
    let tbody = table?.querySelector<HTMLTableSectionElement>('tbody');
    let thead = table?.querySelector<HTMLTableSectionElement>('thead');

    // Если таблицы нет — создаём всю структуру
    if (!table || !tbody || !thead) {
        container.innerHTML = '';

        const wrap = document.createElement('div');
        wrap.className = 'leaderboard-layout';

        const tableWrap = document.createElement('div');
        tableWrap.className = 'leaderboard-table-wrap';

        table = document.createElement('table');
        table.className = 'leaderboard-table';
        thead = document.createElement('thead');
        tbody = document.createElement('tbody');
        table.append(thead, tbody);
        tableWrap.appendChild(table);

        const helpBox = document.createElement('aside');
        helpBox.className = 'leaderboard-help';

        wrap.append(tableWrap, helpBox);
        container.appendChild(wrap);

        // Переключатель режимов сверху
        renderModeSwitch();
    }

    // Содержимое справки зависит от режима
    const helpBox = container.querySelector<HTMLElement>('.leaderboard-help');
    if (helpBox) {
        helpBox.innerHTML = isSeasonMode(currentMode) ? buildSeasonHelpHtml() : buildHelpHtml();
    }

    // Подробное пояснение MMR с примером — под таблицей (только для режимов с MMR).
    renderEloExplain(container, !isSeasonMode(currentMode));

    const columns = columnsFor(currentMode);

    // --- Обновляем THEAD (заголовки со стрелками) ---
    thead.innerHTML = '';
    const headRow = document.createElement('tr');
    const thRank = document.createElement('th');
    thRank.textContent = '#';
    headRow.appendChild(thRank);

    for (const col of columns) {
        const th = document.createElement('th');
        th.textContent = col.label;
        th.dataset.sortKey = col.key;
        th.classList.add('sortable');
        // Колонка тира — узкая, по центру (как в таблице игроков)
        if (col.key === 'tier') th.classList.add('lb-tier-th');

        if (sortKey === col.key) {
            th.classList.add('is-sorted');
            th.classList.toggle('is-sorted-asc', sortDirection === 'asc');
            th.classList.toggle('is-sorted-desc', sortDirection === 'desc');
            th.textContent = col.label + (sortDirection === 'asc' ? ' ▲' : ' ▼');
        }
th.addEventListener('click', () => handleSortClick(col.key));

        headRow.appendChild(th);

        // Колонка расы (иконки из карточки) — сразу после имени, без сортировки.
        if (col.key === 'name') {
            const thRace = document.createElement('th');
            thRace.className = 'lb-race-th';
            thRace.dataset.i18n = 'players.col.race';
            thRace.textContent = t('players.col.race');
            headRow.appendChild(thRace);
        }
    }

    // Пустая колонка-заглушка в конце: тут стрелка-индикатор раскрытия строки
    const thExpand = document.createElement('th');
    thExpand.className = 'lb-expand-th';
    headRow.appendChild(thExpand);

    thead.appendChild(headRow);

    // --- Обновляем TBODY (данные) ---
    detailAbort?.abort();
    detailAbort = null;
    tbody.innerHTML = '';
    players.forEach((p, index) => {
        const tr = document.createElement('tr');
        const place = p.mmr_rank ?? (p.rank != null && p.rank > 0 ? p.rank : index + 1);
        if (place <= 5) tr.classList.add('lb-top-row', `lb-top-${place}`);
        if (place === 1) tr.classList.add('rank-gold', 'lb-champion-row');
        else if (place === 2) tr.classList.add('rank-silver');
        else if (place === 3) tr.classList.add('rank-bronze');

        if (state.user?.player_id && p.player_id === state.user.player_id) {
            tr.classList.add('is-me');
        }

        tr.classList.add('lb-expandable');
        tr.dataset.lbPlayer = String(p.player_id);
        if (expandedPlayerId === p.player_id) tr.classList.add('is-expanded');

        // Клик по строке раскрывает/скрывает график прогресса (кроме кликов по нику-ссылке)
        tr.addEventListener('click', (e) => {
            if ((e.target as HTMLElement).closest('a')) return;
            void togglePlayerDetail(p, tr);
        });

        const tdRank = document.createElement('td');
        tdRank.className = 'lb-rank';
        tdRank.appendChild(buildRankBadge(p, place));
        tr.appendChild(tdRank);

        for (const col of columns) {
            tr.appendChild(buildCell(col.key, col.label, p));
            // Раса — сразу после имени
            if (col.key === 'name') tr.appendChild(buildRaceCell(p.races));
        }

        // Стрелка-индикатор раскрытия строки — в самом конце строки.
        // Рядом иконка графика и подсказка, что именно откроется.
        const tdExpand = document.createElement('td');
        tdExpand.className = 'lb-expand-cell';
        const expandHint = document.createElement('span');
        expandHint.className = 'lb-expand-hint';
        expandHint.appendChild(makeChartIcon());
        const expandMark = document.createElement('span');
        expandMark.className = 'lb-expand-indicator';
        expandMark.setAttribute('aria-hidden', 'true');
        expandMark.title = t('leaderboard.expand_hint');
        tdExpand.append(expandHint, expandMark);
        tr.appendChild(tdExpand);

        tbody.appendChild(tr);
    });

    // Плавное появление
    table.classList.remove('is-loading');
    table.classList.add('just-updated');
    setTimeout(() => table.classList.remove('just-updated'), 300);
}

// ============================================================
// Командный зачёт: таблица связок (2 игрока)
// ============================================================

/** Колонки таблицы связок: у сезонного зачёта нет MMR-колонок. */
function pairColumns(season = false): Array<{ key: PairSortKey; label: string }> {
    if (season) {
        return [
            { key: 'pair', label: t('leaderboard.pair_col') },
            { key: 'games', label: t('leaderboard.games') },
            { key: 'wins', label: t('leaderboard.wins') },
            { key: 'winrate', label: t('leaderboard.winrate') },
            { key: 'avg_place', label: t('leaderboard.avg_place') },
            { key: 'game_days', label: t('leaderboard.pair_days') },
        ];
    }
    return [
        { key: 'pair', label: t('leaderboard.pair_col') },
        { key: 'pair_elo', label: t('leaderboard.pair_elo') },
        { key: 'games', label: t('leaderboard.games') },
        { key: 'wins', label: t('leaderboard.wins') },
        { key: 'winrate', label: t('leaderboard.winrate') },
        { key: 'avg_place', label: t('leaderboard.avg_place') },
    ];
}

function handlePairSortClick(key: PairSortKey): void {
    if (pairSortKey === key) {
        pairSortDir = pairSortDir === 'asc' ? 'desc' : 'asc';
    } else {
        pairSortKey = key;
        pairSortDir = key === 'pair' || key === 'avg_place' ? 'asc' : 'desc';
    }
    const container = document.getElementById('leaderboard-container');
    if (!container) return;
    renderPairs(applyPairSort(cachedPairs), container, isSeasonMode(currentMode));
}

/** Ссылка на игрока внутри строки связки. */
function pairPlayerLink(playerId: number, name: string): HTMLAnchorElement {
    const link = document.createElement('a');
    link.className = 'player-name-link';
    link.href = `/?player=${encodeURIComponent(name)}`;
    link.textContent = name;
    if (state.user?.player_id && playerId === state.user.player_id) {
        link.classList.add('is-me');
    }
    link.addEventListener('click', (e) => {
        e.preventDefault();
        navigateTo(`/?player=${encodeURIComponent(name)}`);
    });
    return link;
}

function renderPairs(pairs: PairEntry[], container: HTMLElement, season = false): void {
    container.innerHTML = '';

    const wrap = document.createElement('div');
    wrap.className = 'leaderboard-layout';

    const tableWrap = document.createElement('div');
    tableWrap.className = 'leaderboard-table-wrap';

    const table = document.createElement('table');
    table.className = 'leaderboard-table leaderboard-table--pairs';

    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    const thRank = document.createElement('th');
    thRank.textContent = '#';
    headRow.appendChild(thRank);

    const columns = pairColumns(season);
    for (const col of columns) {
        const th = document.createElement('th');
        th.textContent = col.label;
        th.classList.add('sortable');
        if (pairSortKey === col.key) {
            th.classList.add('is-sorted');
            th.classList.toggle('is-sorted-asc', pairSortDir === 'asc');
            th.classList.toggle('is-sorted-desc', pairSortDir === 'desc');
            th.textContent = col.label + (pairSortDir === 'asc' ? ' ▲' : ' ▼');
        }
        th.addEventListener('click', () => handlePairSortClick(col.key));
        headRow.appendChild(th);
    }
    const thExpand = document.createElement('th');
    thExpand.className = 'lb-expand-th';
    headRow.appendChild(thExpand);
    thead.appendChild(headRow);

    const tbody = document.createElement('tbody');

    pairs.forEach((p, index) => {
        const place = index + 1;
        const tr = document.createElement('tr');
        if (place <= 5) tr.classList.add('lb-top-row', `lb-top-${place}`);
        if (place === 1) tr.classList.add('rank-gold', 'lb-champion-row');
        else if (place === 2) tr.classList.add('rank-silver');
        else if (place === 3) tr.classList.add('rank-bronze');
        if (
            state.user?.player_id &&
            (p.player_a_id === state.user.player_id || p.player_b_id === state.user.player_id)
        ) {
            tr.classList.add('is-me');
        }

        const key = pairKey(p);
        tr.classList.add('lb-expandable');
        tr.dataset.lbPair = key;
        if (expandedPairKey === key) tr.classList.add('is-expanded');
        tr.addEventListener('click', (e) => {
            if ((e.target as HTMLElement).closest('a')) return;
            void togglePairDetail(p, tr, season);
        });

        const tdRank = document.createElement('td');
        tdRank.className = 'lb-rank';
        const badge = document.createElement('div');
        badge.className = 'lb-rank-badge' + (place === 1 ? ' is-champion' : '');
        const main = document.createElement('div');
        main.className = 'lb-rank-main';
        if (place === 1) {
            const crown = document.createElement('span');
            crown.className = 'lb-rank-crown';
            crown.textContent = '👑';
            crown.setAttribute('aria-hidden', 'true');
            main.appendChild(crown);
        }
        const num = document.createElement('span');
        num.className = 'lb-rank-num';
        num.textContent = String(place);
        main.appendChild(num);
        badge.appendChild(main);
        tdRank.appendChild(badge);
        tr.appendChild(tdRank);

        // Пара игроков.
        const tdPair = document.createElement('td');
        tdPair.className = 'lb-name lb-pair-name';
        const pairInner = document.createElement('div');
        pairInner.className = 'lb-pair-inner';
        pairInner.append(
            pairPlayerLink(p.player_a_id, p.player_a_name),
            document.createTextNode(' + '),
            pairPlayerLink(p.player_b_id, p.player_b_name)
        );
        tdPair.appendChild(pairInner);
        tr.appendChild(tdPair);

        // Числовые колонки: в сезонном зачёте вместо MMR — игровые дни.
        const cells: Array<{ key: PairSortKey; text: string; cls?: string; title?: string }> = season
            ? [
                  { key: 'games', text: String(p.games) },
                  { key: 'wins', text: String(p.wins) },
                  {
                      key: 'winrate',
                      text: p.winrate != null ? `${Math.round(Number(p.winrate))}%` : '—',
                  },
                  {
                      key: 'avg_place',
                      text: p.avg_place != null ? Number(p.avg_place).toFixed(2) : '—',
                  },
                  { key: 'game_days', text: String(p.game_days ?? 0) },
              ]
            : (() => {
                  const pairElo = Math.round(p.pair_elo ?? 0);
                  return [
                      {
                          key: 'pair_elo',
                          text: String(pairElo),
                          cls: 'lb-elo',
                          title: t('leaderboard.pair_elo_hint'),
                      },
                      { key: 'games', text: String(p.games) },
                      { key: 'wins', text: String(p.wins) },
                      {
                          key: 'winrate',
                          text:
                              p.winrate != null
                                  ? `${Math.round(Number(p.winrate))}%`
                                  : '—',
                      },
                      {
                          key: 'avg_place',
                          text: p.avg_place != null ? Number(p.avg_place).toFixed(2) : '—',
                      },
                  ];
              })();

        for (const cell of cells) {
            const label = columns.find((c) => c.key === cell.key)?.label ?? '';
            const td = document.createElement('td');
            td.className = 'lb-stat' + (cell.cls ? ` ${cell.cls}` : '');
            if (cell.title) td.title = cell.title;

            const lbl = document.createElement('span');
            lbl.className = 'lb-mobile-label';
            lbl.textContent = label;
            const val = document.createElement('span');
            val.className = 'lb-value';
            val.textContent = cell.text;

            td.append(lbl, val);
            tr.appendChild(td);
        }

        const tdExpand = document.createElement('td');
        tdExpand.className = 'lb-expand-cell';
        const expandHint = document.createElement('span');
        expandHint.className = 'lb-expand-hint';
        expandHint.appendChild(makeChartIcon());
        const expandMark = document.createElement('span');
        expandMark.className = 'lb-expand-indicator';
        expandMark.setAttribute('aria-hidden', 'true');
        expandMark.title = t('leaderboard.expand_hint');
        tdExpand.append(expandHint, expandMark);
        tr.appendChild(tdExpand);

        tbody.appendChild(tr);
    });

    table.append(thead, tbody);
    tableWrap.appendChild(table);

    if (pairs.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'hint';
        empty.textContent = t('leaderboard.pair_empty');
        tableWrap.appendChild(empty);
        table.classList.add('hidden');
    }

    const helpBox = document.createElement('aside');
    helpBox.className = 'leaderboard-help';
    helpBox.innerHTML = season ? buildSeasonPairHelpHtml() : buildPairHelpHtml();

    wrap.append(tableWrap, helpBox);
    container.appendChild(wrap);

    renderModeSwitch();

    // После перерисовки (сортировка/язык) возвращаем раскрытый график связки.
    if (expandedPairKey) {
        const p = pairs.find((x) => pairKey(x) === expandedPairKey);
        const tr = table.querySelector<HTMLTableRowElement>(
            `tr[data-lb-pair="${expandedPairKey}"]`
        );
        if (p && tr) openPairDetail(p, tr, season);
    }
}

/** Раскрывает/скрывает график прогресса связки под её строкой. */
async function togglePairDetail(
    p: PairEntry,
    tr: HTMLTableRowElement,
    season: boolean,
): Promise<void> {
    const tbody = tr.parentElement;
    if (!tbody) return;

    const key = pairKey(p);
    if (expandedPairKey === key) {
        closePairDetail(tr);
        return;
    }

    tbody.querySelectorAll('tr.is-expanded').forEach((row) => {
        const next = row.nextElementSibling;
        if (next instanceof HTMLTableRowElement && next.dataset.lbPairDetail) {
            closePairDetail(row as HTMLTableRowElement);
        } else {
            row.classList.remove('is-expanded');
        }
    });

    openPairDetail(p, tr, season);
}

/** Под строкой связки создаёт место под график и запускает загрузку истории. */
function openPairDetail(p: PairEntry, tr: HTMLTableRowElement, season: boolean): void {
    const key = pairKey(p);
    expandedPairKey = key;
    tr.classList.add('is-expanded');

    const detailRow = document.createElement('tr');
    detailRow.dataset.lbPairDetail = key;
    detailRow.classList.add('lb-detail-row', 'is-opening');
    const td = document.createElement('td');
    td.colSpan = pairColumns(season).length + 2;
    td.className = 'lb-detail-cell';
    const body = document.createElement('div');
    body.className = 'lb-detail';
    const hint = document.createElement('div');
    hint.className = 'lb-detail-title';
    hint.textContent = t('leaderboard.pair_chart_title');
    body.appendChild(hint);
    const inner = document.createElement('div');
    inner.className = 'lb-detail-body';
    inner.textContent = '…';
    body.appendChild(inner);
    td.appendChild(body);
    detailRow.appendChild(td);
    tr.after(detailRow);

    void loadPairDetailChart(p, inner);
}

/** Закрывает раскрытую строку связки. */
function closePairDetail(tr: HTMLTableRowElement): void {
    tr.classList.remove('is-expanded');
    detailAbort?.abort();
    detailAbort = null;
    expandedPairKey = null;
    const detail = tr.nextElementSibling;
    if (detail instanceof HTMLTableRowElement && detail.dataset.lbPairDetail) {
        detail.classList.remove('is-opening');
        detail.classList.add('is-closing');
        setTimeout(() => detail.remove(), 250);
    }
}

/** Грузит историю MMR связки и рисует график (по умолчанию включён только MMR пары). */
async function loadPairDetailChart(p: PairEntry, inner: HTMLElement): Promise<void> {
    detailAbort?.abort();
    detailAbort = new AbortController();
    const signal = detailAbort.signal;

    try {
        const res = await apiRequest<{ points: PairHistoryPoint[] }>(
            `/api/ratings/leaderboard/pair-history/${p.player_a_id}/${p.player_b_id}`,
            { signal }
        );
        if (signal.aborted) return;

        if (res.points.length === 0) {
            inner.textContent = t('leaderboard.pair_chart_empty');
            return;
        }

        const points: ChartPoint[] = res.points.map((pt) => ({
            date: new Date(pt.played_at),
            elo: 0,
            pair_elo: pt.pair_elo,
            rank: null,
            activity: 0,
            games: pt.games,
            wins: pt.wins,
            quality: 0,
            avg_place: pt.avg_place,
            winrate: pt.winrate,
            game: {
                place: pt.place,
                total: pt.total,
                isWinner: pt.is_winner,
                isTeam: true,
                eloDelta: pt.elo_delta,
                format: pt.format,
                map: pt.map,
            },
        }));

        inner.replaceChildren(renderProgressChart(points, PAIR_CHART_SERIES, pairChartEnabled));
    } catch {
        if (!signal.aborted) inner.textContent = t('leaderboard.chart_error');
    }
}

/** Пояснение к зачёту связок. */
function buildPairHelpHtml(): string {
    return `
    <h3>${t('leaderboard.pair_help_title')}</h3>
    <p>${t('leaderboard.pair_help_intro')}</p>
    <ul>
      <li>${t('leaderboard.pair_help_sum')}</li>
      <li>${t('leaderboard.pair_help_together')}</li>
      <li>${t('leaderboard.pair_help_games')}</li>
    </ul>
    <p class="hint">${t('leaderboard.pair_help_outro')}</p>
  `;
}

/** Пояснение к сезонному зачёту связок (без MMR). */
function buildSeasonPairHelpHtml(): string {
    return `
    <h3>${t('leaderboard.pair_help_title')}</h3>
    <p>${t('leaderboard.pair_help_intro')}</p>
    <ul>
      <li>${t('leaderboard.season_pair_help_rank')}</li>
      <li>${t('leaderboard.pair_help_games')}</li>
      <li>${t('leaderboard.season_pair_help_days')}</li>
    </ul>
    <p class="hint">${t('leaderboard.pair_help_outro')}</p>
  `;
}

/**
 * Плавно раскрывает/скрывает под строкой график прогресса игрока.
 * При раскрытии грузим историю игр этого игрока и строим SVG-график.
 */
async function togglePlayerDetail(p: LeaderboardEntry, tr: HTMLTableRowElement): Promise<void> {
    const tbody = tr.parentElement;
    if (!tbody) return;

    // Если эта строка уже раскрыта — скрываем её
    if (expandedPlayerId === p.player_id) {
        closeDetail(tr);
        return;
    }

    // Закрываем любую другую раскрытую строку
    tbody.querySelectorAll('tr.is-expanded').forEach((row) => {
        const next = row.nextElementSibling;
        if (next instanceof HTMLTableRowElement && next.dataset.lbDetail) {
            closeDetail(row as HTMLTableRowElement);
        } else {
            row.classList.remove('is-expanded');
        }
    });

    openDetail(p, tr);
}

/** Раскрывает строку и строит под ней график прогресса (грузит историю игр игрока). */
function openDetail(p: LeaderboardEntry, tr: HTMLTableRowElement): void {
    expandedPlayerId = p.player_id;
    tr.classList.add('is-expanded');

    const detailRow = document.createElement('tr');
    detailRow.dataset.lbDetail = String(p.player_id);
    detailRow.classList.add('lb-detail-row', 'is-opening');
    const td = document.createElement('td');
    td.colSpan = columnsFor(currentMode).length + 4;
    td.className = 'lb-detail-cell';
    const body = document.createElement('div');
    body.className = 'lb-detail';
    const hint = document.createElement('div');
    hint.className = 'lb-detail-title';
    hint.textContent = t('leaderboard.chart_title');
    body.appendChild(hint);
    const inner = document.createElement('div');
    inner.className = 'lb-detail-body';
    inner.textContent = '…';
    body.appendChild(inner);
    td.appendChild(body);
    detailRow.appendChild(td);
    tr.after(detailRow);

    void loadDetailChart(p, inner);
}

/** Закрывает раскрытую строку с плавным уходом деталей. */
function closeDetail(tr: HTMLTableRowElement): void {
    tr.classList.remove('is-expanded');
    detailAbort?.abort();
    detailAbort = null;
    expandedPlayerId = null;
    const detail = tr.nextElementSibling;
    if (detail instanceof HTMLTableRowElement && detail.dataset.lbDetail) {
        detail.classList.remove('is-opening');
        detail.classList.add('is-closing');
        setTimeout(() => detail.remove(), 250);
    }
}

/**
 * Параметры «осторожного» Elo (учёт недоигранности).
 * Должны совпадать с константами в SQL-функции recalculate_all_ratings().
 *   C — сколько игр нужно, чтобы апостериорное среднее сравнялось со «сырым» Elo (масштаб доверия к старту 1500).
 *   RD0 — стартовая неопределённость рейтинга (в очках Elo).
 *   Z — сколько сигм неопределённости вычитаем (осторожная нижняя граница).
 */
const ELO_PRIOR_GAMES = 5;
const ELO_RD0 = 150;
const ELO_Z = 1;

/**
 * Осторожный Elo: апостериорное среднее, сжатое к 1500 по числу игр, минус штраф
 * за неопределённость. Мало игр → рейтинг близок к 1500 и сильно снижен, поэтому
 * игрок с парой матчей не висит в топе над опытными.
 */
function confidenceElo(raw: number, games: number): number {
    if (games <= 0) return 1500 - ELO_Z * ELO_RD0;
    const mean = 1500 + (raw - 1500) * (games / (games + ELO_PRIOR_GAMES));
    const penalty = ELO_Z * ELO_RD0 * Math.sqrt(ELO_PRIOR_GAMES / (ELO_PRIOR_GAMES + games));
    return Math.round(mean - penalty);
}

/** Загружает историю игр игрока и рисует SVG-график + разбивку MMR по местам. */
async function loadDetailChart(p: LeaderboardEntry, inner: HTMLElement): Promise<void> {
    detailAbort?.abort();
    detailAbort = new AbortController();
    const signal = detailAbort.signal;

    try {
        const [gamesRes, ranksRes] = await Promise.all([
            apiRequest<{ games: GameListItem[] }>(
                `/api/games?player_id=${p.player_id}&limit=200`,
                { signal }
            ),
            // Место в рейтинге по MMR после каждой игры (для линии «место»).
            apiRequest<{ ranks: Array<{ game_id: number; rank: number }> }>(
                `/api/ratings/leaderboard/rank-history/${p.player_id}`,
                { signal }
            ).catch(() => ({ ranks: [] as Array<{ game_id: number; rank: number }> })),
        ]);
        if (signal.aborted) return;

        const rankByGame = new Map(ranksRes.ranks.map((r) => [r.game_id, r.rank]));

        // Только игры этого игрока, отсортированные по дате (сначала старые)
        const points: ChartPoint[] = [];
        let elo = 1500; // стартовое Elo системы
        let activity = 0;
        let games = 0;
        let wins = 0;
        let placeSum = 0;
        let placeCount = 0;

        // Вклад в сырой MMR по диапазонам мест: топ (1–2), середина (3–6), низ (7+).
        const buckets: EloBuckets = {
            top: { delta: 0, games: 0 },
            mid: { delta: 0, games: 0 },
            bottom: { delta: 0, games: 0 },
            unknown: { delta: 0, games: 0 },
        };

        const own = (g: GameListItem) => g.participants.find((gp) => gp.player_id === p.player_id);
        const gamesAsc = [...gamesRes.games]
            .filter((g) => own(g))
            .sort((a, b) => new Date(a.played_at).getTime() - new Date(b.played_at).getTime());

        for (const g of gamesAsc) {
            const me = own(g)!;
            games += 1;
            if (me.is_winner) wins += 1;
            if (me.elo_delta != null) elo += me.elo_delta;
            if (me.activity_delta != null) activity += Number(me.activity_delta);

            const scale = g.participants.length;
            const place = me.is_winner ? 1 : me.eliminated_at != null ? scale - me.eliminated_at + 1 : null;
            if (place != null) {
                placeSum += place;
                placeCount += 1;
            }

            // Копим дельты MMR по диапазонам мест (только игры с рассчитанной дельтой).
            if (me.elo_delta != null) {
                const bucket =
                    place == null ? buckets.unknown
                        : place <= 2 ? buckets.top
                            : place <= 6 ? buckets.mid
                                : buckets.bottom;
                bucket.delta += me.elo_delta;
                bucket.games += 1;
            }

            points.push({
                date: new Date(g.played_at),
                elo: confidenceElo(elo, games),
                pair_elo: null,
                rank: rankByGame.get(g.id) ?? null,
                activity,
                games,
                wins,
                quality: activity / games,
                avg_place: placeCount ? placeSum / placeCount : null,
                winrate: (wins / games) * 100,
                game: {
                    // Индивидуальное место (в команде победа общая, а место своё)
                    place: me.eliminated_at != null ? scale - me.eliminated_at + 1 : place,
                    total: scale,
                    isWinner: me.is_winner,
                    isTeam: g.is_team,
                    eloDelta: me.elo_delta,
                    format: g.format_name,
                    map: g.map_name,
                },
            });
        }

        if (points.length === 0) {
            inner.textContent = t('leaderboard.chart_empty');
            return;
        }

        inner.replaceChildren(renderEloBreakdown(buckets), renderProgressChart(points));
    } catch {
        if (!signal.aborted) inner.textContent = t('leaderboard.chart_error');
    }
}

/** Вклад в MMR по диапазонам мест: накопленная дельта и число игр. */
interface EloBucket {
    delta: number;
    games: number;
}

interface EloBuckets {
    top: EloBucket;
    mid: EloBucket;
    bottom: EloBucket;
    unknown: EloBucket;
}

/**
 * Мини-разбивка: сколько сырого MMR игрок набрал/потерял на местах 1–2, 3–6 и 7+.
 * Показывает, за счёт чего (и где) складывается текущий рейтинг.
 */
function renderEloBreakdown(buckets: EloBuckets): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'lb-breakdown';

    const title = document.createElement('div');
    title.className = 'lb-breakdown-title';
    title.textContent = t('leaderboard.breakdown_title');
    wrap.appendChild(title);

    const rowsData: Array<{ label: string; b: EloBucket }> = [
        { label: t('leaderboard.breakdown_top'), b: buckets.top },
        { label: t('leaderboard.breakdown_mid'), b: buckets.mid },
        { label: t('leaderboard.breakdown_bottom'), b: buckets.bottom },
    ];
    if (buckets.unknown.games > 0) {
        rowsData.push({ label: t('leaderboard.breakdown_unknown'), b: buckets.unknown });
    }

    // Масштаб баров — по максимальному |вкладу| среди диапазонов.
    const maxAbs = Math.max(1, ...rowsData.map((r) => Math.abs(r.b.delta)));

    const list = document.createElement('div');
    list.className = 'lb-breakdown-rows';

    for (const { label, b } of rowsData) {
        const row = document.createElement('div');
        row.className = 'lb-breakdown-row';

        const lab = document.createElement('span');
        lab.className = 'lb-breakdown-label';
        lab.textContent = label;

        const track = document.createElement('div');
        track.className = 'lb-breakdown-track';
        const center = document.createElement('div');
        center.className = 'lb-breakdown-center';
        const bar = document.createElement('div');
        bar.className = 'lb-breakdown-bar';
        const widthPct = Math.min(50, (Math.abs(b.delta) / maxAbs) * 50);
        if (b.delta >= 0) {
            bar.classList.add('is-plus');
            bar.style.left = '50%';
        } else {
            bar.classList.add('is-minus');
            bar.style.left = `${50 - widthPct}%`;
        }
        bar.style.width = `${widthPct}%`;
        track.append(center, bar);

        const val = document.createElement('span');
        val.className = 'lb-breakdown-val ' + (b.delta >= 0 ? 'is-plus' : 'is-minus');
        val.textContent = `${b.delta >= 0 ? '+' : ''}${b.delta}`;
        const cnt = document.createElement('span');
        cnt.className = 'lb-breakdown-count';
        cnt.textContent = ` (${b.games})`;
        val.appendChild(cnt);

        row.append(lab, track, val);
        list.appendChild(row);
    }

    wrap.appendChild(list);

    // Итог — сумма всех дельт (сырой MMR = 1500 + итого).
    const total = rowsData.reduce((s, r) => s + r.b.delta, 0);
    const totalRow = document.createElement('div');
    totalRow.className = 'lb-breakdown-total';
    const totalLab = document.createElement('span');
    totalLab.textContent = t('leaderboard.breakdown_total');
    const totalVal = document.createElement('span');
    totalVal.className = total >= 0 ? 'is-plus' : 'is-minus';
    totalVal.textContent = `${total >= 0 ? '+' : ''}${total}`;
    totalRow.append(totalLab, totalVal);
    wrap.appendChild(totalRow);

    return wrap;
}

/** Точка графика прогресса: кумулятивные метрики игрока на момент игры. */
interface ChartPoint {
    date: Date;
    elo: number;
    /** MMR связки (только для графика пары). */
    pair_elo: number | null;
    /** Место в рейтинге по MMR после этой игры (меньше — лучше). */
    rank: number | null;
    activity: number;
    games: number;
    wins: number;
    quality: number;
    avg_place: number | null;
    winrate: number;
    /** Событие-игра, на которой построена точка (маркер на графике + блок в тултипе). */
    game: ChartGameEvent;
}

/** Краткий итог игры для игрока. */
interface ChartGameEvent {
    place: number | null;
    total: number;
    isWinner: boolean;
    isTeam: boolean;
    eloDelta: number | null;
    format: string | null;
    map: string | null;
}

/** Класс цвета маркера: победа / верхняя половина / нижняя половина / место неизвестно. */
function gameEventClass(ev: ChartGameEvent): string {
    if (ev.isWinner) return 'is-win';
    if (ev.place == null) return 'is-unknown';
    return ev.place <= Math.ceil(ev.total / 2) ? 'is-top' : 'is-bottom';
}

type SeriesKey =
    | 'elo'
    | 'pair_elo'
    | 'rank'
    | 'activity'
    | 'games'
    | 'wins'
    | 'quality'
    | 'avg_place'
    | 'winrate';

interface SeriesDef {
    key: SeriesKey;
    color: string;
    /** Ключ перевода метки (резолвится при рендере, чтобы язык менялся на ходу). */
    labelKey: TranslationKey;
    /** Включена по умолчанию? Качество/ср. место/винрейт — выключены. */
    onByDefault: boolean;
    /** Формат значения для легенды и тултипа. */
    fmt: (v: number) => string;
    /** Меньше — лучше (ср. место): на графике значение инвертируется, чтобы линия росла вместе с качеством. */
    lowerIsBetter?: boolean;
}

/** Все доступные линии графика. */
const CHART_SERIES: SeriesDef[] = [
    { key: 'elo', color: '#3498db', labelKey: 'leaderboard.chart_elo', onByDefault: true, fmt: (v) => v.toFixed(0) },
    { key: 'pair_elo', color: '#d4af37', labelKey: 'leaderboard.pair_elo', onByDefault: false, fmt: (v) => v.toFixed(0) },
    { key: 'rank', color: '#e84393', labelKey: 'leaderboard.chart_rank', onByDefault: true, fmt: (v) => `#${v.toFixed(0)}`, lowerIsBetter: true },
    { key: 'activity', color: '#f1c40f', labelKey: 'leaderboard.activity', onByDefault: false, fmt: (v) => v.toFixed(1) },
    { key: 'games', color: '#9b59b6', labelKey: 'leaderboard.chart_games', onByDefault: false, fmt: (v) => v.toFixed(0) },
    { key: 'wins', color: '#2ecc71', labelKey: 'leaderboard.chart_wins', onByDefault: false, fmt: (v) => v.toFixed(0) },
    { key: 'quality', color: '#e67e22', labelKey: 'leaderboard.quality', onByDefault: false, fmt: (v) => v.toFixed(2) },
    { key: 'avg_place', color: '#1abc9c', labelKey: 'leaderboard.avg_place', onByDefault: false, fmt: (v) => v.toFixed(2), lowerIsBetter: true },
    { key: 'winrate', color: '#e74c3c', labelKey: 'leaderboard.winrate', onByDefault: false, fmt: (v) => v.toFixed(1) + '%' },
];

/** Какие линии сейчас включены (клик по легенде переключает). */
const chartEnabled = new Set<SeriesKey>(CHART_SERIES.filter((s) => s.onByDefault).map((s) => s.key));

/** Линии графика связки: MMR пары включён по умолчанию, остальное — по клику. */
const PAIR_CHART_SERIES = CHART_SERIES.filter((s) =>
    ['pair_elo', 'games', 'wins', 'winrate', 'avg_place'].includes(s.key)
);
const pairChartEnabled = new Set<SeriesKey>(['pair_elo']);

/**
 * Геометрия графика. На телефоне viewBox делаем узким и почти равным
 * реальной ширине — тогда подписи и линии не сжимаются в нечитаемую кашу.
 */
interface ChartGeo {
    W: number;
    H: number;
    padL: number;
    padR: number;
    padT: number;
    padB: number;
    plotW: number;
    plotH: number;
}

function chartGeometry(): ChartGeo {
    const narrow = typeof window !== 'undefined' && window.innerWidth <= 700;
    const W = narrow ? 340 : 900;
    const H = narrow ? 230 : 250;
    const padL = narrow ? 34 : 42;
    const padR = narrow ? 12 : 18;
    const padT = narrow ? 12 : 14;
    const padB = narrow ? 28 : 34;
    return { W, H, padL, padR, padT, padB, plotW: W - padL - padR, plotH: H - padT - padB };
}

/** Короткий формат даты для оси X: ДД.ММ.ГГ. */
function fmtDateShort(d: Date): string {
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    return `${dd}.${mm}.${yy}`;
}

function fmtDateFull(d: Date): string {
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
}

/** Строит интерактивный SVG-график: линии, ось X с датами, легенда-переключатель, тултип. */
function renderProgressChart(
    points: ChartPoint[],
    series: SeriesDef[] = CHART_SERIES,
    enabled: Set<SeriesKey> = chartEnabled,
): HTMLElement {
    const g = chartGeometry();
    const wrap = document.createElement('div');
    wrap.className = 'lb-chart';

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', `0 0 ${g.W} ${g.H}`);
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    svg.classList.add('lb-chart-svg');

    const n = points.length;
    const xOf = (i: number) => g.padL + (n === 1 ? g.plotW / 2 : (i / (n - 1)) * g.plotW);

    // Горизонтальная сетка + оси
    for (let gi = 0; gi <= 4; gi++) {
        const y = g.padT + (gi / 4) * g.plotH;
        const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        line.setAttribute('x1', String(g.padL));
        line.setAttribute('y1', String(y));
        line.setAttribute('x2', String(g.W - g.padR));
        line.setAttribute('y2', String(y));
        line.classList.add('lb-chart-grid');
        svg.appendChild(line);
    }

    // Линии выбранных серий
    const enabledDefs = series.filter((s) => enabled.has(s.key));
    const yFor: Record<SeriesKey, (i: number) => number> = {} as Record<SeriesKey, (i: number) => number>;
    for (const s of enabledDefs) {
        const values = points.map((pt) => pt[s.key]);
        const nums = values.filter((v): v is number => v !== null);
        // Нет ни одного числового значения (напр. всё «место не указано») — линию не рисуем
        if (nums.length === 0) {
            yFor[s.key] = () => g.padT + g.plotH;
            continue;
        }
        const min = Math.min(...nums);
        const max = Math.max(...nums);
        const range = max - min || 1;
        // lowerIsBetter (ср. место) — инвертируем: меньшее значение выше на графике
        yFor[s.key] = (v: number) =>
            g.padT + g.plotH - ((s.lowerIsBetter ? max - v : v - min) / range) * g.plotH;

        const pts = points
            .map((pt, i) => {
                const v = pt[s.key];
                if (v === null) return '';
                return `${xOf(i)},${yFor[s.key](v)}`;
            })
            .filter(Boolean)
            .join(' ');

        if (!pts) continue;

        const poly = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
        poly.setAttribute('points', pts);
        poly.setAttribute('fill', 'none');
        poly.setAttribute('stroke', s.color);
        poly.setAttribute('stroke-width', '2');
        poly.classList.add('lb-chart-line');
        poly.dataset.series = s.key;
        svg.appendChild(poly);
    }

    // Ось X: стартовая и текущая даты (и, если есть место, средняя точка)
    const xAxis = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    xAxis.setAttribute('x1', String(g.padL));
    xAxis.setAttribute('y1', String(g.H - g.padB));
    xAxis.setAttribute('x2', String(g.W - g.padR));
    xAxis.setAttribute('y2', String(g.H - g.padB));
    xAxis.classList.add('lb-chart-axis');
    svg.appendChild(xAxis);

    const axisLabels: Array<{ x: number; text: string }> = [
        { x: g.padL, text: fmtDateShort(points[0]!.date) },
    ];
    if (n > 2) {
        const mid = Math.floor(n / 2);
        axisLabels.push({ x: xOf(mid), text: fmtDateShort(points[mid]!.date) });
    }
    if (n > 1) {
        axisLabels.push({ x: g.W - g.padR, text: fmtDateShort(points[n - 1]!.date) });
    }
    for (const lab of axisLabels) {
        const txt = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        txt.setAttribute('x', String(lab.x));
        txt.setAttribute('y', String(g.H - g.padB + 16));
        txt.setAttribute('text-anchor', lab.x === g.padL ? 'start' : lab.x === g.W - g.padR ? 'end' : 'middle');
        txt.textContent = lab.text;
        txt.classList.add('lb-chart-xlabel');
        svg.appendChild(txt);
    }

    // Маркеры игр на оси X: цвет — результат (победа / верх / низ таблицы мест)
    const markerR = n > 80 ? 2.5 : 3.5;
    const markers = points.map((pt, i) => {
        const c = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        c.setAttribute('cx', String(xOf(i)));
        c.setAttribute('cy', String(g.H - g.padB));
        c.setAttribute('r', String(markerR));
        c.classList.add('lb-chart-event', gameEventClass(pt.game));
        svg.appendChild(c);
        return c;
    });
    let activeMarker: SVGCircleElement | null = null;

    // Вертикальная линия-прицел (перемещается при наведении)
    const crosshair = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    crosshair.setAttribute('y1', String(g.padT));
    crosshair.setAttribute('y2', String(g.H - g.padB));
    crosshair.classList.add('lb-chart-crosshair');
    crosshair.setAttribute('x1', '0');
    crosshair.setAttribute('x2', '0');
    svg.appendChild(crosshair);

    // Тултип (HTML-оверлей поверх SVG)
    const tooltip = document.createElement('div');
    tooltip.className = 'lb-chart-tooltip';
    wrap.appendChild(tooltip);

    // Наведение: определяем ближайшую игру по X и показываем значения
    svg.addEventListener('pointermove', (e) => {
        const rect = svg.getBoundingClientRect();
        if (rect.width === 0) return;
        const frac = (e.clientX - rect.left) / rect.width; // 0..1 от всего viewBox
        const plotX = g.padL + frac * g.W - g.padL; // в координатах plot
        const index = Math.round(((plotX - g.padL) / g.plotW) * (n - 1));
        const i = Math.max(0, Math.min(n - 1, index));
        const cx = xOf(i);
        crosshair.setAttribute('x1', String(cx));
        crosshair.setAttribute('x2', String(cx));
        crosshair.classList.add('is-visible');

        const pt = points[i]!;
        tooltip.innerHTML = '';
        const dateRow = document.createElement('div');
        dateRow.className = 'lb-chart-tt-date';
        dateRow.textContent = fmtDateFull(pt.date);
        tooltip.appendChild(dateRow);
        tooltip.appendChild(renderGameSummary(pt.game));

        activeMarker?.classList.remove('is-active');
        const marker = markers[i] ?? null;
        activeMarker = marker;
        marker?.classList.add('is-active');

        for (const s of enabledDefs) {
            const v = pt[s.key];
            const row = document.createElement('div');
            row.className = 'lb-chart-tt-row';
            const dot = document.createElement('i');
            dot.className = 'lb-chart-dot';
            dot.style.background = s.color;
            const lab = document.createElement('span');
            lab.className = 'lb-chart-tt-label';
            lab.textContent = t(s.labelKey);
            const val = document.createElement('span');
            val.className = 'lb-chart-tt-value';
            val.textContent = v === null ? '—' : s.fmt(v);
            row.append(dot, lab, val);
            tooltip.appendChild(row);
        }
        // Позиционируем: слева/справа от курсора, сдвигая к краям
        tooltip.style.display = 'block';
        const leftPct = (frac * 100);
        tooltip.style.left = leftPct > 62 ? `${leftPct - 18}%` : `${leftPct + 6}%`;
        tooltip.style.top = '8px';
    });

    svg.addEventListener('pointerleave', () => {
        crosshair.classList.remove('is-visible');
        tooltip.style.display = 'none';
        activeMarker?.classList.remove('is-active');
        activeMarker = null;
    });

    // Легенда: клик по пункту включает/выключает линию
    const legend = document.createElement('div');
    legend.className = 'lb-chart-legend';

    for (const s of series) {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'lb-chart-legend-item' + (enabled.has(s.key) ? ' is-on' : '');
        item.title = t('leaderboard.chart_toggle');
        const dot = document.createElement('i');
        dot.className = 'lb-chart-dot';
        dot.style.background = s.color;
        const txt = document.createElement('span');
        const last = points[points.length - 1]?.[s.key] ?? null;
        txt.textContent = `${t(s.labelKey)}: ${last === null ? '—' : s.fmt(last)}`;
        item.append(dot, txt);
        item.addEventListener('click', () => {
            if (enabled.has(s.key)) enabled.delete(s.key);
            else enabled.add(s.key);
            // Пересобираем график с новым набором линий
            const body = wrap.parentElement;
            const fresh = renderProgressChart(points, series, enabled);
            if (body) {
                wrap.remove();
                body.appendChild(fresh);
            }
        });
        legend.appendChild(item);
    }

    wrap.append(svg, legend);
    return wrap;
}

/** Блок тултипа «итог игры»: формат · карта, место/победа, изменение MMR. */
function renderGameSummary(ev: ChartGameEvent): HTMLElement {
    const box = document.createElement('div');
    box.className = 'lb-chart-tt-game';

    const meta = [ev.format, ev.map].filter(Boolean).join(' · ');
    if (meta) {
        const m = document.createElement('div');
        m.className = 'lb-chart-tt-game-meta';
        m.textContent = meta;
        box.appendChild(m);
    }

    const res = document.createElement('div');
    res.className = 'lb-chart-tt-game-result ' + gameEventClass(ev);
    const parts: string[] = [];
    if (ev.isWinner) parts.push(t('leaderboard.chart_win') + (ev.isTeam ? ` (${t('leaderboard.chart_team')})` : ''));
    // Место не дублируем для FFA-победителя (оно и так 1-е)
    if (!(ev.isWinner && !ev.isTeam)) {
        parts.push(`${t('leaderboard.chart_place')} ${ev.place ?? '?'}/${ev.total}`);
    }
    res.textContent = parts.join(' · ');

    if (ev.eloDelta != null) {
        const d = document.createElement('span');
        d.className = 'lb-chart-tt-game-delta ' + (ev.eloDelta >= 0 ? 'is-plus' : 'is-minus');
        d.textContent = `MMR ${ev.eloDelta >= 0 ? '+' : ''}${ev.eloDelta}`;
        res.appendChild(d);
    }
    box.appendChild(res);
    return box;
}

/** Набор колонок для режима: сезонные зачёты без ELO/активности. */
function columnsFor(mode: Mode): Array<{ label: string; key: SortKey }> {
    if (isSeasonMode(mode)) {
        return [
            { label: t('ratings.col.tier'), key: 'tier' },
            { label: t('players.col.name'), key: 'name' },
            { label: t('leaderboard.wins'), key: 'wins' },
            { label: t('leaderboard.winrate'), key: 'winrate' },
            { label: t('leaderboard.games'), key: 'games' },
            { label: t('leaderboard.avg_place'), key: 'avg_place' },
        ];
    }
    return [
        { label: t('ratings.col.tier'), key: 'tier' },
        { label: t('players.col.name'), key: 'name' },
        { label: "" + t('players.col.elo') + "", key: 'elo' },
        { label: t('leaderboard.activity'), key: 'activity' },
        { label: t('leaderboard.games'), key: 'games' },
        { label: t('leaderboard.wins'), key: 'wins' },
        { label: t('leaderboard.winrate'), key: 'winrate' },
        { label: t('leaderboard.avg_place'), key: 'avg_place' },
    ];
}

/** Бейдж тира (буква F..S) или null, если тир не указан. */
function buildTierBadge(value: number | null): HTMLSpanElement | null {
    const letter = tierLetter(value);
    if (!letter) return null;

    const badge = document.createElement('span');
    badge.className = 'tier-badge';
    badge.dataset.tier = String(Math.max(1, Math.min(7, Math.round(value as number))));
    badge.textContent = letter;
    badge.title = t('ratings.col.tier');
    return badge;
}

/** Ячейка расы — иконки всех рас игрока из карточки (или из игр, если оценок нет). */
function buildRaceCell(races: Race[] | undefined): HTMLTableCellElement {
    const td = document.createElement('td');
    td.className = 'lb-race-cell';

    const list = (races ?? []).filter((r): r is Race => Boolean(r));
    if (list.length === 0) {
        const empty = document.createElement('span');
        empty.className = 'lb-race-empty';
        empty.textContent = '—';
        td.appendChild(empty);
        return td;
    }

    const inner = document.createElement('div');
    inner.className = 'lb-race-inner';
    for (const r of list) {
        const img = document.createElement('img');
        img.src = RACE_ICON_URL[r];
        img.alt = RACE_LABELS[r];
        img.title = RACE_LABELS[r];
        img.className = `race-icon race-icon-${r}`;
        inner.appendChild(img);
    }
    td.appendChild(inner);
    return td;
}

/** Номер места, смещение за последний игровой день и стаж на текущем месте. */
function buildRankBadge(p: LeaderboardEntry, place: number): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'lb-rank-badge';
    if (place === 1) wrap.classList.add('is-champion');

    const main = document.createElement('span');
    main.className = 'lb-rank-main';

    if (place === 1) {
        const crown = document.createElement('span');
        crown.className = 'lb-rank-crown';
        crown.textContent = '👑';
        crown.setAttribute('aria-hidden', 'true');
        main.appendChild(crown);
    }

    const num = document.createElement('span');
    num.className = 'lb-rank-num';
    num.textContent = String(place);
    main.appendChild(num);

    const delta = p.rank_delta;
    if (delta != null && delta !== 0) {
        const move = document.createElement('span');
        move.className = 'lb-rank-move ' + (delta > 0 ? 'is-up' : 'is-down');
        move.textContent = `${delta > 0 ? '▲' : '▼'}${Math.abs(delta)}`;
        const moveText = t(delta > 0 ? 'leaderboard.rank_up' : 'leaderboard.rank_down', {
            n: Math.abs(delta),
        });
        move.title = p.rank_prev != null ? `${moveText} · ${t('leaderboard.rank_prev', { n: p.rank_prev })}` : moveText;
        main.appendChild(move);
    } else if (p.rank_prev === null && p.rank_delta === null && p.place_days != null) {
        const fresh = document.createElement('span');
        fresh.className = 'lb-rank-new';
        fresh.textContent = t('leaderboard.rank_new_short');
        fresh.title = t('leaderboard.rank_new');
        main.appendChild(fresh);
    }

    wrap.appendChild(main);

    const days = p.place_days;
    if (days != null) {
        const held = document.createElement('span');
        held.className = 'lb-rank-held';
        if (place <= 5) held.classList.add('is-top5');
        if (place === 1) {
            held.classList.add('is-reign');
            const full = document.createElement('span');
            full.className = 'lb-rank-reign-full';
            full.textContent =
                days > 0
                    ? t('leaderboard.rank_reign', { n: days })
                    : t('leaderboard.rank_reign_today');
            const short = document.createElement('span');
            short.className = 'lb-rank-reign-short';
            short.textContent = days > 0 ? t('leaderboard.rank_held', { n: days }) : t('leaderboard.rank_held_today');
            held.append(full, short);
            held.title = t('leaderboard.rank_reign_title', { n: days });
        } else if (days > 0) {
            held.textContent = t('leaderboard.rank_held', { n: days });
            held.title = t('leaderboard.rank_held_title', { n: days });
        } else {
            held.textContent = t('leaderboard.rank_held_today');
            held.title = t('leaderboard.rank_held_title', { n: 0 });
        }
        wrap.appendChild(held);
    }

    return wrap;
}

function buildCell(key: SortKey, label: string, p: LeaderboardEntry): HTMLTableCellElement {
    const td = document.createElement('td');

    // Тир — бейджем перед ником (как в таблице игроков)
    if (key === 'tier') {
        td.className = 'lb-tier-cell';
        const badge = buildTierBadge(p.ghost_tier);
        if (badge) {
            td.appendChild(badge);
        } else {
            const empty = document.createElement('span');
            empty.className = 'tier-badge tier-badge--empty';
            empty.textContent = '—';
            td.appendChild(empty);
        }
        return td;
    }

    if (key === 'name') {
        td.className = 'lb-name';
        // На телефоне отдельная колонка скрыта — бейдж ставим в строку с ником
        const tierInline = buildTierBadge(p.ghost_tier);
        if (tierInline) {
            tierInline.classList.add('lb-tier-inline');
            td.appendChild(tierInline);
        }
        const link = document.createElement('a');
        link.href = `/?player=${encodeURIComponent(p.name)}`;
        link.className = 'player-name-link';
        link.textContent = p.name;
        link.addEventListener('click', (e) => {
            e.preventDefault();
            navigateTo(`/?player=${encodeURIComponent(p.name)}`);
        });
        td.appendChild(link);
        // Для своей строки — локализованная приписка «(вы)»
        if (state.user?.player_id && p.player_id === state.user.player_id) {
            const me = document.createElement('span');
            me.className = 'lb-me-suffix';
            me.textContent = t('players.me_suffix');
            td.appendChild(me);
        }
        if (p.aka) {
            const aka = document.createElement('span');
            aka.className = 'player-aka';
            aka.textContent = ` aka ${p.aka}`;
            aka.title = t('leaderboard.aka_hint');
            aka.setAttribute('role', 'button');
            aka.setAttribute('tabindex', '0');
            aka.setAttribute('aria-expanded', 'false');
            const toggleAka = () => {
                const open = td.classList.toggle('is-aka-open');
                aka.setAttribute('aria-expanded', open ? 'true' : 'false');
                aka.title = t(open ? 'leaderboard.aka_collapse' : 'leaderboard.aka_hint');
            };
            aka.addEventListener('click', (ev) => {
                // Клик по aka раскрывает его, а не строку с графиком.
                ev.preventDefault();
                ev.stopPropagation();
                toggleAka();
            });
            aka.addEventListener('keydown', (ev) => {
                if (ev.key !== 'Enter' && ev.key !== ' ') return;
                ev.preventDefault();
                ev.stopPropagation();
                toggleAka();
            });
            td.appendChild(aka);
        }
        return td;
    }

    // Остальные ячейки: подпись (для мобильного) + значение
    td.className = 'lb-stat';
    if (key === 'activity') td.classList.add('lb-activity');
    if (key === 'elo') td.classList.add('lb-elo');
    if (key === 'activity') td.classList.add('activity-cell');

    const lbl = document.createElement('span');
    lbl.className = 'lb-mobile-label';
    lbl.textContent = label;
    td.appendChild(lbl);

    const val = document.createElement('span');
    val.className = 'lb-value';

    let text = '';
    switch (key) {
        case 'activity':
            text = p.activity_score.toFixed(1);
            break;
        case 'elo':
            text = String(p.elo);
            break;
        case 'games':
            text = String(p.games_played);
            break;
        case 'wins':
            text = String(p.wins);
            break;
        case 'winrate':
            text = `${p.winrate}%`;
            break;
        case 'avg_place':
            text = p.avg_place !== null ? p.avg_place.toFixed(2) : '—';
            break;
    }
    val.textContent = text;
    td.appendChild(val);

    return td;
}

/** Маленькая SVG-иконка графика для ячейки-подсказки раскрытия строки. */
function makeChartIcon(): SVGSVGElement {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 16 16');
    svg.setAttribute('width', '20');
    svg.setAttribute('height', '20');
    svg.setAttribute('aria-hidden', 'true');
    const poly = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
    poly.setAttribute('points', '1,13 5,9 8,11 12,4 15,6');
    poly.setAttribute('fill', 'none');
    poly.setAttribute('stroke', 'currentColor');
    poly.setAttribute('stroke-width', '2');
    poly.setAttribute('stroke-linecap', 'round');
    poly.setAttribute('stroke-linejoin', 'round');
    svg.appendChild(poly);
    return svg;
}

function buildHelpHtml(): string {
    return `
    <h3>${t('leaderboard.help_title')}</h3>
    <p>${t('leaderboard.help_intro')}</p>
    <ul>
      <li>${t('leaderboard.help_activity')}</li>
      <li>${t('leaderboard.help_place')}</li>
      <li>${t('leaderboard.help_not_elim')}</li>
      <li>${t('leaderboard.help_winner')}</li>
      <li>${t('leaderboard.help_early')}</li>
      <li>${t('leaderboard.help_provisional')}</li>
    </ul>
    <p class="hint">${t('leaderboard.help_outro')}</p>
  `;
}

function buildSeasonHelpHtml(): string {
    return `
    <h3>${t('leaderboard.season_help_title')}</h3>
    <p>${t('leaderboard.season_help_text')}</p>
  `;
}

/** Показывает/обновляет подробное пояснение MMR с примером под таблицей. */
function renderEloExplain(container: HTMLElement, show: boolean): void {
    let el = container.querySelector<HTMLElement>('#leaderboard-explain');
    if (!show) {
        el?.remove();
        return;
    }
    if (!el) {
        el = document.createElement('section');
        el.id = 'leaderboard-explain';
        el.className = 'lb-explain';
        container.appendChild(el);
    }
    el.innerHTML = buildEloExplainHtml();
}

/** Подробное пояснение системы MMR с формулой и разбором примера. */
function buildEloExplainHtml(): string {
    return `
    <h3>${t('leaderboard.explain_title')}</h3>
    <p>${t('leaderboard.explain_intro')}</p>
    <h4>${t('leaderboard.explain_how_title')}</h4>
    <ul>
      <li>${t('leaderboard.explain_how_1')}</li>
      <li>${t('leaderboard.explain_how_2')}</li>
      <li>${t('leaderboard.explain_how_3')}</li>
      <li>${t('leaderboard.explain_how_4')}</li>
      <li>${t('leaderboard.explain_how_5')}</li>
      <li>${t('leaderboard.explain_how_6')}</li>
    </ul>
    <h4>${t('leaderboard.explain_formula_title')}</h4>
    <ul>
      <li>${t('leaderboard.explain_e')}</li>
      <li>${t('leaderboard.explain_delta')}</li>
      <li>${t('leaderboard.explain_s')}</li>
      <li>${t('leaderboard.explain_confidence')}</li>
    </ul>
    <h4>${t('leaderboard.explain_example_title')}</h4>
    <p>${t('leaderboard.explain_example_intro')}</p>
    <table class="lb-explain-table">
      <thead><tr><th></th><th>Emperror</th><th>Seigneur</th></tr></thead>
      <tbody>
        <tr><td>${t('leaderboard.explain_row_games')}</td><td>40</td><td>33</td></tr>
        <tr><td>${t('leaderboard.explain_row_firsts')}</td><td>3</td><td>7</td></tr>
        <tr><td>${t('leaderboard.avg_place')}</td><td>4.25</td><td>3.58</td></tr>
        <tr><td>${t('leaderboard.explain_row_raw')}</td><td>1525</td><td>1502</td></tr>
        <tr><td>${t('leaderboard.explain_row_mmr')}</td><td class="is-plus">1472</td><td>1447</td></tr>
      </tbody>
    </table>
    <p>${t('leaderboard.explain_buckets_note')}</p>
    <table class="lb-explain-table">
      <thead><tr><th>${t('leaderboard.explain_place_col')}</th><th>Emperror</th><th>Seigneur</th></tr></thead>
      <tbody>
        <tr><td>${t('leaderboard.breakdown_top')}</td><td class="is-plus">+406 <span class="mut">(9)</span></td><td class="is-plus">+453 <span class="mut">(11)</span></td></tr>
        <tr><td>${t('leaderboard.breakdown_mid')}</td><td class="is-plus">+19 <span class="mut">(23)</span></td><td class="is-minus">−301 <span class="mut">(19)</span></td></tr>
        <tr><td>${t('leaderboard.breakdown_bottom')}</td><td class="is-minus">−400 <span class="mut">(8)</span></td><td class="is-minus">−150 <span class="mut">(3)</span></td></tr>
      </tbody>
    </table>
    <p class="hint">${t('leaderboard.explain_conclusion')}</p>
    <h4>${t('leaderboard.explain_reading_title')}</h4>
    <p>${t('leaderboard.explain_reading')}</p>
  `;
}